/**
 * Installs the calling route's ribbon command handler for as long as the route
 * is active, through the tokenised registry so a late unmount cannot take the
 * slot away from the route that replaced it.
 */

import { useEffect, useRef } from 'react';
import {
  registerHostRibbonCommandHandler,
  unregisterHostRibbonCommandHandler,
  type HostRibbonCommandHandler,
} from '../../lib/hostRibbon';

export const useHostRibbonCommands = (
  owner: string,
  active: boolean,
  dispatch: HostRibbonCommandHandler
): void => {
  // The registration outlives any single render of the handler, so commands are
  // forwarded to the latest one instead of re-registering on every change.
  const latestDispatch = useRef(dispatch);
  latestDispatch.current = dispatch;

  useEffect(() => {
    if (!active) {
      return undefined;
    }

    const token = registerHostRibbonCommandHandler(owner, (command) =>
      latestDispatch.current(command)
    );

    return () => {
      unregisterHostRibbonCommandHandler(token);
    };
  }, [active, owner]);
};
